import React, {useCallback, useEffect, useState, useMemo} from "react";
import {Button, Card, Collapse} from "react-bootstrap";
import {Location, Photo, SettingsDoc, User, Visit} from "../../types/Types";
import {collection, doc, getDoc, getDocs, limit, orderBy, query, where} from "@firebase/firestore";
import {db, storage, functions} from "../../../firebase/FirebaseProvider";
import {ref, uploadBytes, getDownloadURL} from "firebase/storage";
import LocationNav from "./LocationNav";
import LocationDetails from "./LocationDetails";
import LocationDetailsV2 from "./LocationDetailsV2";
import {generateTags, generateUUID, isPhotoEnabled, PHOTOS_LIMIT, resizeImage, logDataLoaded} from "../../util/Util";
import LocationCheckIn from "./LocationCheckIn";
import PhotoRow from "./PhotoRow";
import {httpsCallable} from "@firebase/functions";
import PageTracking from "../../auth/PageTracking";
import {Link, useParams} from "react-router-dom";
import PhotoTagInput from "../../photos/PhotoTagInput";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faCalendar, faHeart, faPlus} from "@fortawesome/free-solid-svg-icons";
import {V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import V2PhotoRow from "../../shared/v2/V2PhotoRow";

interface Props {
    currentUser: User | undefined
    usersMap: Map<string, User>
    settings: SettingsDoc
    likesMap: Map<string, boolean>
}

function LocationPhotos(props: Props) {
    const { id } = useParams<{ id: string }>();

    const [location, setLocation] = useState<Location | undefined>(undefined);
    const [photos, setPhotos] = useState<Array<Photo> | undefined>(undefined);
    const [fullSizePhotoToUpload, setFullSizePhotoToUpload] = useState<Blob | undefined>();
    const [smallSizePhotoToUpload, setSmallSizePhotoToUpload] = useState<Blob | undefined>();
    const [displayPhoto, setDisplayPhoto] = useState<string | undefined>();
    const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false);
    const [feedback, setFeedback] = useState<string | undefined>();
    const [tags, setTags] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [isAddPhotoOpen, setIsAddPhotoOpen] = useState<boolean>(false);
    const [sortType, setSortType] = useState<string>('date');
    const [visited, setVisited] = useState<boolean>(false);

    const updatePhotoLikeCount = useCallback((photoId: string, adjustment: number) => {
        setPhotos(prevPhotos => {
            if (!prevPhotos) return prevPhotos;
            return prevPhotos.map(photo => {
                if (photo.id === photoId) {
                    return {
                        ...photo,
                        likeCount: (photo.likeCount || 0) + adjustment
                    };
                }
                return photo;
            });
        });
    }, []);

    const loadPhotos = useCallback(async () => {
        if (!location) return;
        
        try {
            const loadedPhotos: Array<Photo> = [];
            const photosOnceCollection = query(collection(db, 'photos'),
                where('locationId', '==', location.id),
                where('hidden', '==', false),
                orderBy('createdAt', 'desc'),
                limit(PHOTOS_LIMIT));
            const snapshot = await getDocs(photosOnceCollection);
            snapshot.forEach((doc) => {
                const tempPhoto = doc.data() as Photo;
                tempPhoto.id = doc.id;
                loadedPhotos.push(tempPhoto);
            });
            logDataLoaded('location photos', loadedPhotos.length);
            setPhotos(loadedPhotos);
        } catch (error) {
            console.error("Error loading photos:", error);
            setPhotos([]);
        }
    }, [location]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            const locationRef = doc(db, 'locations', id);
            const docSnap = await getDoc(locationRef);
            if (docSnap.exists()) {
                const locationData = docSnap.data() as Location;
                locationData.id = docSnap.id;
                setLocation(locationData);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!id || !props.currentUser?.id) return;
        
        (async () => {
            const visitQuery = query(
                collection(db, 'visits'),
                where('locationId', '==', id),
                where('userId', '==', props.currentUser!.id)
            );
            const visitSnap = await getDocs(visitQuery);
            if (!visitSnap.empty) {
                const visitData = visitSnap.docs[0].data() as Visit;
                setVisited(visitData.visited || false);
            }
        })();
    }, [id, props.currentUser?.id]);

    const toggleVisited = useCallback(() => {
        if (!id) return;
        
        const toggleVisitedFunction = httpsCallable(functions, 'toggleVisitedV2');
        toggleVisitedFunction({locationId: id}).then(async (response) => {
            const result = response.data as { status: number; code: string };
            if (result.status === 403) {
                alert(result.code);
                return;
            }
            
            const locationRef = doc(db, 'locations', id);
            const docSnap = await getDoc(locationRef);
            if (docSnap.exists()) {
                const locationData = docSnap.data() as Location;
                locationData.id = docSnap.id;
                setLocation(locationData);
            }
            
            setVisited(prev => !prev);
        }).catch((error) => {
            console.error('Error toggling visited:', error);
            alert('Failed to update visit status');
        });
    }, [id]);

    useEffect(() => {
        if (!location) return;
        loadPhotos();
    }, [location, loadPhotos]);

    const preparePhotoForUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        const fileSize = file.size / (1024 * 1024); // Convert to MB
        if (fileSize > 30) {
            alert('File size exceeds 30 MB');
            return;
        }

        const displayUrl = URL.createObjectURL(file);
        setFullSizePhotoToUpload(file);
        setDisplayPhoto(displayUrl);

        resizeImage(file, 720, 540).then((resizedImage) => {
            setSmallSizePhotoToUpload(resizedImage as Blob);
        }).catch((err) => {
            console.error("Error resizing image:", err);
        });
    };

    const uploadPhotos = useCallback(async (fullSizeId: string, smallSizeId: string) => {
        if (!location || !fullSizePhotoToUpload || !smallSizePhotoToUpload) return;

        const fullSizeStorageRef = ref(storage, `photos/locations/${location.id}/${fullSizeId}`);
        const smallSizeStorageRef = ref(storage, `photos/locations/${location.id}/thumbnails/${smallSizeId}`);

        await uploadBytes(fullSizeStorageRef, fullSizePhotoToUpload);
        const fullSizeUrl = await getDownloadURL(fullSizeStorageRef);
        await uploadBytes(smallSizeStorageRef, smallSizePhotoToUpload);
        const smallSizeUrl = await getDownloadURL(smallSizeStorageRef);

        return { fullSizeUrl, smallSizeUrl };
    }, [location, fullSizePhotoToUpload, smallSizePhotoToUpload]);

    const savePhoto = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        
        if (!fullSizePhotoToUpload || !smallSizePhotoToUpload) {
            alert("Please attach a photo.");
            return;
        }

        setUploadingPhoto(true);
        try {
            const fullSizeId = generateUUID();
            const smallSizeId = generateUUID();
            const urls = await uploadPhotos(fullSizeId, smallSizeId);
            
            if (urls && location) {
                const savePhotoFn = httpsCallable(functions, 'savePhoto');
                await savePhotoFn({
                    photo: {
                        fullSizeUrl: urls.fullSizeUrl,
                        fullSizeId: fullSizeId,
                        smallSizeUrl: urls.smallSizeUrl,
                        smallSizeId: smallSizeId,
                        locationId: location.id,
                        locationName: location.name,
                        tags: generateTags(tags),
                        description: description.trim() || undefined,
                    }
                });
                
                // Reload photos instead of reloading the page
                await loadPhotos();
                
                // Reset form state
                setIsAddPhotoOpen(false);
                setFullSizePhotoToUpload(undefined);
                setSmallSizePhotoToUpload(undefined);
                setDisplayPhoto(undefined);
                setTags("");
                setDescription("");
                setFeedback("Photo saved successfully!");
                
                // Clear feedback after 3 seconds
                setTimeout(() => setFeedback(undefined), 3000);
            }
        } catch (error) {
            console.error("Error saving photo:", error);
            setFeedback("Error saving photo. Please try again.");
        } finally {
            setUploadingPhoto(false);
        }
    }, [location, fullSizePhotoToUpload, smallSizePhotoToUpload, tags, description, uploadPhotos, loadPhotos]);

    const handleSort = (newSortType: string) => {
        setSortType(newSortType);
    };

    // Sort photos based on sortType
    const displayPhotos = useMemo(() => {
        if (!photos) return undefined;
        
        // Create a shallow copy to avoid mutating the original array
        const photosToSort = [...photos];
        
        // Sort based on sortType
        if(sortType === 'likes') {
            return photosToSort.sort((a: Photo, b: Photo) => (b.likeCount || 0) - (a.likeCount || 0));
        } else {
            // Default sort by date (newest first)
            return photosToSort.sort((a: Photo, b: Photo) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
        }
    }, [photos, sortType]);

    const isUiV2 = !!props.currentUser?.enableUiV2;

    if (isUiV2) {
        const currentUser = props.currentUser!;
        return (
            <>
                <PageTracking />
                <LocationDetailsV2
                    location={location}
                    currentUser={currentUser}
                    likesMap={props.likesMap}
                    visited={visited}
                    onToggleVisited={toggleVisited}
                    variant="header"
                />
                {location && <V2GlassPanel className="location-v2-detail-toolbar">
                    <Link className="location-v2-back-button" to={`/location/${location.id}/comments`}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Pub
                    </Link>
                    <div className="location-v2-detail-stats">
                        <V2GlassSurface className="location-v2-detail-stat">
                            <span>Photos</span>
                            <b>{displayPhotos?.length || 0}</b>
                        </V2GlassSurface>
                        {location && !currentUser.socialDisabled && isPhotoEnabled(currentUser, props.settings) && (
                            <button className={`location-v2-back-button${isAddPhotoOpen ? " location-v2-toolbar-button-active" : ""}`} type="button" onClick={() => setIsAddPhotoOpen((isOpen) => !isOpen)}>
                                <FontAwesomeIcon icon={faPlus} />
                                Photo
                            </button>
                        )}
                        <button className={`location-v2-back-button${sortType === "date" ? " location-v2-toolbar-button-active" : ""}`} disabled={sortType === "date"} type="button" onClick={() => handleSort("date")}>
                            <FontAwesomeIcon icon={faCalendar} />
                        </button>
                        <button className={`location-v2-back-button${sortType === "likes" ? " location-v2-toolbar-button-active" : ""}`} disabled={sortType === "likes"} type="button" onClick={() => handleSort("likes")}>
                            <FontAwesomeIcon icon={faHeart} />
                        </button>
                    </div>
                </V2GlassPanel>}
                {isAddPhotoOpen && <V2GlassPanel className="location-v2-photo-add-card">
                    <div className="location-v2-photo-add-grid">
                        <label className="location-v2-photo-upload" htmlFor="image-input">
                            {!displayPhoto && <span>Attach photo</span>}
                            {displayPhoto && <img src={displayPhoto} alt="Preview" />}
                        </label>
                        <input
                            onChange={preparePhotoForUpload}
                            type="file"
                            className="input-file d-none"
                            name="image"
                            id="image-input"
                            accept="image/*"
                        />
                        <div className="location-v2-photo-add-fields">
                            <PhotoTagInput tags={tags} setTags={setTags} />
                            <textarea
                                className="form-control tag-input"
                                rows={2}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Add an optional description..."
                            />
                            <div className="location-v2-photo-add-actions">
                                <span>{feedback}</span>
                                <button disabled={uploadingPhoto} onClick={savePhoto} className="location-v2-tag-button" type="button">
                                    {!uploadingPhoto && <span>Save</span>}
                                    {uploadingPhoto && <span>Saving...</span>}
                                </button>
                            </div>
                        </div>
                    </div>
                </V2GlassPanel>}
                <V2GlassPanel className="location-v2-detail-list-card location-v2-photo-gallery-card v2-glass-list-panel">
                    {!displayPhotos && <div className="location-v2-empty-list">Loading...</div>}
                    {displayPhotos && displayPhotos.length === 0 && <div className="location-v2-empty-list">No photos yet</div>}
                    {displayPhotos && <div className="v2-hub-photo-row-list">
                        {displayPhotos.map((photo: Photo) => {
                            const photoUser = props.usersMap.get(photo.userId);
                            return (
                                <V2PhotoRow
                                    key={photo.id}
                                    currentUser={currentUser}
                                    photo={photo}
                                    photoUser={photoUser}
                                    likesMap={props.likesMap}
                                    locationPlacement="none"
                                    onLikeCountChange={updatePhotoLikeCount}
                                />
                            );
                        })}
                    </div>}
                </V2GlassPanel>
            </>
        );
    }

    return (
        <>
            <PageTracking />
            {props.currentUser && (props.currentUser.enableUiV2 ? <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="header"
            /> : <LocationDetails
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
            />)}
            {location && props.currentUser && !props.currentUser.enableUiV2 && !props.currentUser.socialDisabled && 
                <LocationCheckIn currentUser={props.currentUser} location={location}/>}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="location"
            />}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="tags"
            />}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="widgets"
            />}
            {location && 
                <LocationNav settings={props.settings} currentUser={props.currentUser} locationId={location.id}/>}

            {location && props.currentUser && !props.currentUser.socialDisabled && 
             isPhotoEnabled(props.currentUser, props.settings) && isAddPhotoOpen && 
                <Card className="mb-2">
                    <Card.Header className="header-padding" style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                        <span><b>Adding photo</b></span>
                        <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => setIsAddPhotoOpen(false)}
                        >
                            Cancel
                        </button>
                    </Card.Header>
                    <Collapse in={isAddPhotoOpen}>
                        <div>
                            <div style={{padding: "0.8em", paddingTop: "1em"}}>
                                {/* Photo Upload Section */}
                                <fieldset style={{
                                    border: "1px solid rgba(255, 255, 255, 0.2)",
                                    borderRadius: "5px",
                                    padding: "0.5em 1em 1em 1em",
                                    marginBottom: "1em"
                                }}>
                                    <legend style={{
                                        fontSize: "0.85rem",
                                        color: "#999",
                                        width: "auto",
                                        padding: "0 0.5em",
                                        marginBottom: "0"
                                    }}>
                                        Photo
                                    </legend>
                                    <div className="d-flex flex-column align-items-center">
                                        <div>
                                            <div className="photo-display">
                                                {!displayPhoto && 
                                                    <label className="photo-upload-label" htmlFor="image-input">
                                                        Attach<br />Photo
                                                    </label>
                                                }
                                                {displayPhoto &&  
                                                    <label htmlFor="image-input">
                                                        <img 
                                                            id="img-preview" 
                                                            className="thumbnail" 
                                                            src={displayPhoto} 
                                                            alt="Preview"
                                                        />
                                                    </label>
                                                }
                                            </div>
                                        </div>
                                        <div>
                                            <input 
                                                onChange={preparePhotoForUpload}
                                                type="file"
                                                className="input-file d-none"
                                                name="image"
                                                id="image-input"
                                                accept="image/*"
                                            />
                                        </div>
                                    </div>
                                </fieldset>

                                {/* Tags Section */}
                                <fieldset style={{
                                    border: "1px solid rgba(255, 255, 255, 0.2)",
                                    borderRadius: "5px",
                                    padding: "0.5em 1em 1em 1em",
                                    marginBottom: "1em"
                                }}>
                                    <legend style={{
                                        fontSize: "0.85rem",
                                        color: "#999",
                                        width: "auto",
                                        padding: "0 0.5em",
                                        marginBottom: "0"
                                    }}>
                                        Tags
                                    </legend>
                                    <div>
                                        <PhotoTagInput 
                                            tags={tags}
                                            setTags={setTags}
                                        />
                                    </div>
                                </fieldset>

                                {/* Description Section */}
                                <fieldset style={{
                                    border: "1px solid rgba(255, 255, 255, 0.2)",
                                    borderRadius: "5px",
                                    padding: "0.5em 1em 1em 1em",
                                    marginBottom: "1em"
                                }}>
                                    <legend style={{
                                        fontSize: "0.85rem",
                                        color: "#999",
                                        width: "auto",
                                        padding: "0 0.5em",
                                        marginBottom: "0"
                                    }}>
                                        Description
                                    </legend>
                                    <div>
                                        <textarea
                                            className="form-control tag-input"
                                            rows={2}
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Add an optional description for this photo..."
                                        />
                                    </div>
                                </fieldset>

                                <div className="form-group p-0" style={{paddingTop: "0.5em", paddingBottom: "1em"}}>
                                    <div>
                                        <div style={{float: "left"}}>
                                            <span>{feedback}</span>
                                        </div>
                                        <div style={{float: "right", marginBottom: "1em"}}>
                                            <button 
                                                disabled={uploadingPhoto} 
                                                onClick={savePhoto} 
                                                className="btn btn-primary btn-sm"
                                            >
                                                {!uploadingPhoto && <span>Save</span>}
                                                {uploadingPhoto && <span>Saving...</span>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Collapse>
                </Card>
            }

            <Card className="mb-3">
                <Card.Header className="header-padding" style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    {location && props.currentUser && !props.currentUser.socialDisabled && 
                     isPhotoEnabled(props.currentUser, props.settings) && !isAddPhotoOpen && (
                        <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => setIsAddPhotoOpen(true)}
                        >
                            Add Photo
                        </button>
                    )}
                    <div style={{marginLeft: "auto"}}>
                        <Button 
                            className={"btn-sm mr-2 cursor-pointer"} 
                            disabled={sortType === 'date'} 
                            onClick={() => handleSort("date")}
                        >
                            <FontAwesomeIcon icon={faCalendar} />
                        </Button>
                        <Button 
                            className={"btn-sm cursor-pointer"} 
                            disabled={sortType === 'likes'} 
                            onClick={() => handleSort("likes")}
                        >
                            <FontAwesomeIcon icon={faHeart} />
                        </Button>
                    </div>
                </Card.Header>
                <div>
                    {!displayPhotos && 
                        <div className="loading">
                            Loading...
                        </div>
                    }
                    {displayPhotos && displayPhotos.length === 0 &&
                        <div className="loading">
                            No photos yet
                        </div>
                    }
                    <div className="album">
                        {displayPhotos && displayPhotos.map((photo: Photo, index: number) => (
                            props.currentUser && 
                                <PhotoRow
                                    key={photo.id + index}
                                    currentUser={props.currentUser}
                                    photo={photo}
                                    photoUser={props.usersMap.get(photo.userId)}
                                    displayLocation={false}
                                    displayEditButton={true}
                                    likesMap={props.likesMap}
                                    onLikeCountChange={updatePhotoLikeCount}
                                />
                        ))}
                    </div>
                </div>
            </Card>
        </>
    );
}

export default LocationPhotos;
